import { useNavigate } from "react-router-dom";
import { useProperty } from "@/contexts/PropertyContext";
import { Building2, Layers, Plus, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * Iter47 — Property switcher shown in the Partner Portal header + dashboard.
 *
 * For single-hotel partners: renders nothing (no clutter).
 * For chain partners:       renders a dropdown listing all owned properties,
 *                           an "All Properties" aggregate option, and an
 *                           "Add property" action.
 */
export default function PropertySwitcher({ dark = false }) {
  const navigate = useNavigate();
  const { properties, chain, selectedId, isAll, select } = useProperty();

  // Nothing to show if the partner has ≤1 property AND isn't in chain mode.
  if (!properties || properties.length === 0) return null;
  if (properties.length === 1 && !chain?.is_chain) return null;

  const current = isAll ? null : properties.find((p) => p.hotel_id === selectedId) || properties[0];
  const label = isAll ? "All Properties" : (current?.name || "Select property");
  const dot = isAll ? "bg-emerald-400" : ({ green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500", none: "bg-slate-400" }[current?._health || "none"]);

  const btnClasses = dark
    ? "flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold"
    : "flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 text-sm font-semibold";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={btnClasses} data-testid="property-switcher">
          {isAll ? <Layers className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
          <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
          <span className="max-w-[160px] truncate">{label}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <div className="text-xs text-slate-500">Switch property</div>
          {chain?.is_chain && chain?.company_name && (
            <div className="text-[11px] text-slate-400 mt-0.5 truncate">{chain.company_name}</div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {chain?.is_chain && (
          <DropdownMenuItem onClick={() => select("__all__")} data-testid="switcher-all">
            <Layers className="w-4 h-4 mr-2" />
            <div>
              <div className="font-bold">All Properties</div>
              <div className="text-[11px] text-slate-500">Aggregated view · {properties.length} hotels</div>
            </div>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {properties.map((p) => (
          <DropdownMenuItem
            key={p.hotel_id}
            onClick={() => select(p.hotel_id)}
            data-testid={`switcher-${p.hotel_id}`}
            className={selectedId === p.hotel_id ? "bg-emerald-50" : ""}
          >
            <span className={`w-1.5 h-1.5 rounded-full mr-2 ${{ green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500", none: "bg-slate-400" }[p._health || "none"]}`} />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{p.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{p.city}</div>
            </div>
          </DropdownMenuItem>
        ))}
        {chain?.is_chain && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/partner/add-property")} data-testid="switcher-add">
              <Plus className="w-4 h-4 mr-2 text-emerald-600" />
              <span className="font-bold text-emerald-700">Add property</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
